// src/features/adminAuthSlice.js
import { createSlice } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";

const TOKEN_KEY = "adminAuthToken";
const ADMIN_DATA_KEY = "adminData";
const ADMIN_ROLES_KEY = "adminRoles";
const ADMIN_PERMS_KEY = "adminPermissions";

const rawToken = localStorage.getItem(TOKEN_KEY);

// try to hydrate admin from token (if token payload contains useful info)
let initialAdminFromToken = null;
if (rawToken) {
  try {
    initialAdminFromToken = jwtDecode(rawToken);
  } catch (e) {
    localStorage.removeItem(TOKEN_KEY);
  }
}

// also try to load persisted structured data (preferred)
const persistedAdmin = (() => {
  try {
    const s = localStorage.getItem(ADMIN_DATA_KEY);
    return s ? JSON.parse(s) : null;
  } catch (e) {
    return null;
  }
})();

const persistedRoles = (() => {
  try {
    const s = localStorage.getItem(ADMIN_ROLES_KEY);
    return s ? JSON.parse(s) : [];
  } catch (e) {
    return [];
  }
})();

const persistedPermissions = (() => {
  try {
    const s = localStorage.getItem(ADMIN_PERMS_KEY);
    return s ? JSON.parse(s) : [];
  } catch (e) {
    return [];
  }
})();

const adminAuthSlice = createSlice({
  name: "adminAuth",
  initialState: {
    token: rawToken || null,
    admin: persistedAdmin || initialAdminFromToken || null,
    roles: persistedRoles || [],
    permissions: persistedPermissions || [],
    isAuthenticated: !!rawToken,
  },
  reducers: {
    /**
     * payload: { token, admin, roles, permissions }
     * admin, roles, permissions come from your backend login response
     */
    adminLoginSuccess: (state, action) => {
      const { token, admin, roles, permissions } = action.payload;
      state.token = token;
      state.admin = admin || state.admin;
      state.roles = roles || state.roles;
      state.permissions = permissions || state.permissions;
      state.isAuthenticated = true;

      // persist
      if (token) localStorage.setItem(TOKEN_KEY, token);
      if (admin) localStorage.setItem(ADMIN_DATA_KEY, JSON.stringify(admin));
      if (roles) localStorage.setItem(ADMIN_ROLES_KEY, JSON.stringify(roles));
      if (permissions) localStorage.setItem(ADMIN_PERMS_KEY, JSON.stringify(permissions));
    },

    // update current admin object (e.g., after role replace or profile update)
    adminUpdateCurrent: (state, action) => {
      const { admin, roles, permissions } = action.payload;
      if (admin) {
        state.admin = admin;
        localStorage.setItem(ADMIN_DATA_KEY, JSON.stringify(admin));
      }
      if (roles) {
        state.roles = roles;
        localStorage.setItem(ADMIN_ROLES_KEY, JSON.stringify(roles));
      }
      if (permissions) {
        state.permissions = permissions;
        localStorage.setItem(ADMIN_PERMS_KEY, JSON.stringify(permissions));
      }
    },

    adminLogout: (state) => {
      state.token = null;
      state.admin = null;
      state.roles = [];
      state.permissions = [];
      state.isAuthenticated = false;
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(ADMIN_DATA_KEY);
      localStorage.removeItem(ADMIN_ROLES_KEY);
      localStorage.removeItem(ADMIN_PERMS_KEY);
    },
  },
});

export const { adminLoginSuccess, adminLogout, adminUpdateCurrent } = adminAuthSlice.actions;
export default adminAuthSlice.reducer;
